import { useState, useMemo } from "react";
import moment from "moment-timezone";
import {
  useListFreightImages,
  useGetFreightImageUpload,
  useUpdateFreightImageCustomer,
  useDeleteFreightImage,
  FreightImageUpload,
  getFreightImageStatusLabel,
  getFreightImageStatusColor,
  FreightImageUploadStatusType,
  isStatusFinalDownloadable,
} from "../../lib/api/freight/index";
import { useListCustomers } from "../../lib/api/customer";
import { DataTable } from "../../components/ui/DataTable/DataTable";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { MultiSelect, SelectItem as MultiSelectItem } from "../../components/ui/selectMulti";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { DataTableColumn } from "@/components/ui/DataTable/types";
import { Combobox } from "@/components/ui/combobox";
import { Eye, Download, ExternalLink, Trash2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { downloadFromS3 } from "@/lib/file";


// Component for action buttons
function ImageActionsCell({
  uploadId,
  status,
  onPreview,
  onDelete,
}: {
  uploadId: string;
  status: FreightImageUploadStatusType;
  onPreview: () => void;
  onDelete: () => void;
}) {
  const { toast } = useToast();
  const [isLoadingAction, setIsLoadingAction] = useState(false);
  
  const { refetch } = useGetFreightImageUpload({
    uploadId,
    enabled: false, // Never auto-fetch
  });
  const willHaveAnLoadableImage = isStatusFinalDownloadable(status);

  const handleAction = async (action: "preview" | "download" | "open") => {
    setIsLoadingAction(true);
    
    try {
      const result = await refetch();
      
      if (!result.data?.success) {
        toast({
          title: "Failed to load image",
          description: "Could not retrieve image details.",
          variant: "destructive",
        });
        return;
      }

      const presignedUrl = result.data.data.presigned_image_url;

      if (!presignedUrl) {
        toast({
          title: "Image not available",
          description: "The image file could not be found or is not ready yet.",
          variant: "destructive",
        });
        return;
      }

      switch (action) {
        case "preview":
          onPreview();
          break;
        case "download": {
          const filename =
            result.data.data.final_document?.filename ||
            "image";
          const downloadResult = await downloadFromS3(presignedUrl, filename);
          if (downloadResult.success) {
            toast({
              title: "Download started",
              description: "Your image is being downloaded.",
            });
          } else {
            toast({
              title: "Download failed",
              description: "Failed to download the image. Please try again.",
              variant: "destructive",
            });
          }
          break;
        }
        case "open":
          window.open(presignedUrl, "_blank");
          break;
      }
    } finally {
      setIsLoadingAction(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleAction("preview")}
        disabled={willHaveAnLoadableImage || isLoadingAction}
        title="Preview image"
      >
        <Eye className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleAction("download")}
        disabled={willHaveAnLoadableImage || isLoadingAction}
        title="Download image"
      >
        <Download className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => handleAction("open")}
        disabled={willHaveAnLoadableImage || isLoadingAction}
        title="Open in new tab"
      >
        <ExternalLink className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={onDelete}
        title="Delete image"
        className="text-red-600 hover:text-red-700 hover:bg-red-50"
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}

export function FreightImagesDashboard() {
  const [limit, setLimit] = useState(30);
  const [offset, setOffset] = useState(0);
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<FreightImageUploadStatusType | undefined>();
  const [previewImageId, setPreviewImageId] = useState<string | null>(null);
  const [editingFreightImage, setEditingFreightImage] = useState<FreightImageUpload | null>(null);
  const [deletingFreightImage, setDeletingFreightImage] = useState<FreightImageUpload | null>(null);

  // Fetch customers for filter
  const customersRes = useListCustomers({ minimal: true });

  // Build query params for freight images
  const queryParams = useMemo(() => {
    const params: {
      limit: number;
      offset: number;
      date_from?: string;
      date_to?: string;
      status?: FreightImageUploadStatusType;
      customer_ids?: string;
    } = { limit, offset };

    if (dateFrom) {
      params.date_from = dateFrom;
    }
    if (dateTo) {
      params.date_to = dateTo;
    }
    if (statusFilter) {
      params.status = statusFilter;
    }
    if (selectedCustomerIds.length > 0) {
      params.customer_ids = selectedCustomerIds.join(",");
    }

    return params;
  }, [limit, offset, dateFrom, dateTo, statusFilter, selectedCustomerIds]);

  // Fetch freight images
  const freightImagesRes = useListFreightImages(queryParams);

  const customers = customersRes.data?.success ? customersRes.data.data : [];

  // Convert customers to MultiSelect options
  const customerOptions: MultiSelectItem[] = useMemo(() => {
    return customers.map((customer: { id: string; long_name: string }) => ({
      value: customer.id,
      label: customer.long_name,
    }));
  }, [customers]);

  const data = freightImagesRes.data?.success ? freightImagesRes.data.data.uploads : [];
  const pagination = freightImagesRes.data?.success ? freightImagesRes.data.data.pagination : null;
  
  // Calculate pagination values
  const currentPage = pagination ? Math.floor(pagination.offset / pagination.limit) + 1 : 1;
  const totalPages = pagination ? Math.ceil(pagination.total / pagination.limit) : 1;
  const hasNextPage = pagination ? (pagination.offset + pagination.limit) < pagination.total : false;
  const hasPrevPage = pagination ? pagination.offset > 0 : false;

  // Reset to first page when filters change
  const handleFilterChange = (callback: () => void) => {
    callback();
    setOffset(0);
  };

  const columns: DataTableColumn<FreightImageUpload>[] = [
    {
      accessorKey: "presigned_thumbnail_image_url",
      header: "Preview",
      cell: ({ row }) => {
        const thumbnailUrl = row.original.presigned_thumbnail_image_url;
        return (
          <div className="w-16 h-16 flex items-center justify-center">
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt="Thumbnail"
                className="w-16 h-16 object-cover rounded border border-gray-200"
              />
            ) : (
              <div className="w-16 h-16 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-xs text-gray-400">
                --
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Upload Date",
      cell: ({ row }) => {
        return (
          <span className="text-sm">
            {moment
              .tz(row.original.created_at, "America/Chicago")
              .format("MM/DD/YYYY HH:mm a")}
          </span>
        );
      },
    },
    {
      accessorKey: "relation_customer",
      header: "Customer",
      cell: ({ row }) => {
        const customer = row.original.relation_customer;
        return (
          <span className="text-sm">
            {customer ? customer.long_name : "--"}
          </span>
        );
      },
    },
    {
      accessorKey: "AuthUser.name",
      accessorFn: (row) => row.AuthUser?.name,
      header: "Uploaded By",
    },
      /* {
  *   accessorKey: "final_document",
  *   header: "Filename",
  *   cell: ({ row }) => {
  *     const finalDoc = row.original.final_document;
  *     const doc = finalDoc;
  *     return (
  *       <span className="text-sm font-mono">
  *         {doc ? doc.filename : "N/A"}
  *       </span>
  *     );
  *   },
  * }, */
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge className={getFreightImageStatusColor(status)}>
            {getFreightImageStatusLabel(status)}
          </Badge>
        );
      },
    },
    {
      accessorKey: "status_message",
      header: "Message",
      cell: ({ row }) => {
        const message = row.original.status_message;
        const subtype = row.original.status_subtype;
        return (
          <span className="text-sm text-gray-600">
            {message || subtype || "—"}
          </span>
        );
      },
    },
    {
      accessorKey: "actions",
      header: "Actions",
      disableRowClick: true,
      cell: ({ row }) => {
        return (
          <ImageActionsCell
            status={row.original.status}
            uploadId={row.original.id}
            onPreview={() => setPreviewImageId(row.original.id)}
            onDelete={() => setDeletingFreightImage(row.original)}
          />
        );
      },
    },
  ];

  // Only show skeleton on initial customer load (needed for filters)
  if (customersRes.isLoading && !customersRes.data) {
    return (
      <div className="flex flex-col space-y-4 p-4">
        <Skeleton className="h-8 w-[300px]" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (customersRes.error) {
    return (
      <div className="flex flex-col space-y-2 p-4">
        <div className="text-red-600">
          Failed to load customers. Please refresh and try again.
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-6 p-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Freight Images</h1>
        <p className="text-sm text-gray-500 mt-1">
          View and manage freight image uploads
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date From */}
          <div className="space-y-2">
            <Label htmlFor="date-from">Date From</Label>
            <Input
              id="date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => handleFilterChange(() => setDateFrom(e.target.value))}
              placeholder="YYYY-MM-DD"
            />
          </div>

          {/* Date To */}
          <div className="space-y-2">
            <Label htmlFor="date-to">Date To</Label>
            <Input
              id="date-to"
              type="date"
              value={dateTo}
              onChange={(e) => handleFilterChange(() => setDateTo(e.target.value))}
              placeholder="YYYY-MM-DD"
            />
          </div>

          {/* Status Filter */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={statusFilter || "all"}
              onValueChange={(value) => {
                handleFilterChange(() => {
                  if (value === "all") {
                    setStatusFilter(undefined);
                  } else {
                    setStatusFilter(value as FreightImageUploadStatusType);
                  }
                });
              }}
            >
              <SelectTrigger id="status">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending_upload">Pending Upload</SelectItem>
                <SelectItem value="uploaded">Uploaded</SelectItem>
                <SelectItem value="scanning">Scanning</SelectItem>
                <SelectItem value="quarantined">Quarantined</SelectItem>
                <SelectItem value="resizing">Resizing</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Customer Filter */}
          <div className="space-y-2">
            <Label htmlFor="customer">Customers</Label>
            <MultiSelect
              values={selectedCustomerIds}
              setValues={(values) => handleFilterChange(() => setSelectedCustomerIds(values))}
              options={customerOptions}
            />
          </div>
        </div>

        {/* Clear Filters */}
        {(dateFrom || dateTo || statusFilter || selectedCustomerIds.length > 0) && (
          <div className="flex justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                handleFilterChange(() => {
                  setDateFrom("");
                  setDateTo("");
                  setStatusFilter(undefined);
                  setSelectedCustomerIds([]);
                });
              }}
            >
              Clear All Filters
            </Button>
          </div>
        )}
      </div>

      {/* Results Count and Page Size */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          {freightImagesRes.isLoading ? (
            "Loading..."
          ) : (
            <>
              Showing {data.length} of{" "}
              {pagination?.total || 0}{" "}
              total images
              {pagination && (
                <span className="ml-2">
                  (Page {currentPage} of {totalPages})
                </span>
              )}
            </>
          )}
        </div>
        
        {/* Page Size Selector */}
        <div className="flex items-center gap-2">
          <Label htmlFor="page-size" className="text-sm">Items per page:</Label>
          <Select
            value={limit.toString()}
            onValueChange={(value) => {
              setLimit(Number(value));
              setOffset(0);
            }}
          >
            <SelectTrigger id="page-size" className="w-20">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="30">30</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="">
        <DataTable
          data={data}
          columns={columns}
          enableSorting={true}
          enableGrouping
          enableMultiSort
          enableColumnVisibility
          enableFiltering={true}
          stickyHeaders
          filterStyle="select"
          filterMode="internal"
          defaultSorting={[{ id: 'created_at', desc: true }]}
          rowClickable
          onRowClick={(row) => setEditingFreightImage(row)}
          emptyMessage={
            freightImagesRes.isLoading
              ? "Loading freight images..."
              : freightImagesRes.error
              ? "Failed to load freight images. Please try again."
              : "No freight images found"
          }
        />
      </div>

      {/* Pagination Controls */}
      {pagination && pagination.total > 0 && (
        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg p-4">
          <div className="text-sm text-gray-600">
            Showing {offset + 1} to {Math.min(offset + limit, pagination.total)} of {pagination.total} results
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(0)}
              disabled={!hasPrevPage || freightImagesRes.isLoading}
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.max(0, offset - limit))}
              disabled={!hasPrevPage || freightImagesRes.isLoading}
            >
              Previous
            </Button>
            
            <div className="text-sm text-gray-600 px-4">
              Page {currentPage} of {totalPages}
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(offset + limit)}
              disabled={!hasNextPage || freightImagesRes.isLoading}
            >
              Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setOffset(Math.floor((pagination.total - 1) / limit) * limit)}
              disabled={!hasNextPage || freightImagesRes.isLoading}
            >
              Last
            </Button>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      <ImagePreviewModal
        uploadId={previewImageId}
        onClose={() => setPreviewImageId(null)}
      />

      {/* Edit Customer Modal */}
      <EditCustomerModal
        freightImage={editingFreightImage}
        onClose={() => setEditingFreightImage(null)}
      />

      {/* Delete Confirmation Modal */}
      <DeleteImageModal
        freightImage={deletingFreightImage}
        onClose={() => setDeletingFreightImage(null)}
      />
    </div>
  );
}

// Preview Modal Component
function ImagePreviewModal({
  uploadId,
  onClose,
}: {
  uploadId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useGetFreightImageUpload({
    uploadId: uploadId || "",
    enabled: !!uploadId,
  });

  const presignedUrl = data?.success ? data.data.presigned_image_url : null;
  const filename = data?.success
    ? data.data.final_document?.filename
    : null;

  return (
    <Dialog open={!!uploadId} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>{filename || "Image Preview"}</DialogTitle>
        </DialogHeader>
        <div className="flex items-center justify-center min-h-[400px]">
          {isLoading ? (
            <div className="text-gray-500">Loading image...</div>
          ) : !presignedUrl ? (
            <div className="text-red-600">
              Image not available. The file could not be found or is not ready yet.
            </div>
          ) : (
            <img
              src={presignedUrl}
              alt={filename || "Freight image"}
              className="max-w-full max-h-[70vh] object-contain"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Edit Customer Modal Component
function EditCustomerModal({
  freightImage,
  onClose,
}: {
  freightImage: FreightImageUpload | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
    freightImage?.relation_customer?.id || null
  );

  const customersRes = useListCustomers({ minimal: true });
  const customers = customersRes.data?.success ? customersRes.data.data : [];

  const customerOptions = useMemo(() => {
    return customers.map((customer: { id: string; long_name: string }) => ({
      value: customer.id,
      label: customer.long_name,
    }));
  }, [customers]);

  const { mutateAsync: updateCustomer, isLoading: isPending } = useUpdateFreightImageCustomer();

  const handleSubmit = async () => {
    if (!freightImage || !selectedCustomerId) return;

    try {
      const result = await updateCustomer({
        id: freightImage.freight_image_id,
        customer_id: selectedCustomerId,
      });

      if (result.success) {
        toast({
          title: "Customer updated",
          description: "The freight image customer has been updated successfully.",
        });
        onClose();
      } else {
        throw new Error(result.message || "Failed to update customer");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update image's customer. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={!!freightImage} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Customer</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div className="space-y-2 grid">
            <Label htmlFor="customer">Customer</Label>
            <Combobox
              options={customerOptions}
              value={selectedCustomerId || ""}
              onValueChange={(value) => setSelectedCustomerId(value || null)}
              placeholder="Select a customer"
              isModal
              virtualizeList
              popoverClassName="w-[500px]"
              dwimOnChange
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isPending}>
              {isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Delete Confirmation Modal Component
function DeleteImageModal({
  freightImage,
  onClose,
}: {
  freightImage: FreightImageUpload | null;
  onClose: () => void;
}) {
  const { toast } = useToast();
  const { mutateAsync: deleteFreightImage, isLoading: isDeleting } = useDeleteFreightImage();

  const handleDelete = async () => {
    if (!freightImage) return;

    try {
      const result = await deleteFreightImage(freightImage.freight_image_id);

      if (result.success) {
        toast({
          title: "Image deleted",
          description: "The freight image has been deleted successfully.",
        });
        onClose();
      } else {
        throw new Error(result.message || "Failed to delete image");
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete image. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={!!freightImage} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete Freight Image</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Are you sure you want to delete this freight image? This action cannot be undone.
          </p>

          {freightImage && (
            <div className="rounded-lg border bg-muted/50 p-4">
              <div className="text-sm">
                <span className="text-muted-foreground">Customer: </span>
                {freightImage.relation_customer?.long_name || "--"}
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">Status: </span>
                {getFreightImageStatusLabel(freightImage.status)}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDelete} 
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
